import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { IconButton, Toolbar } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ReorderIcon from "@mui/icons-material/Reorder";
import AppsIcon from "@mui/icons-material/Apps";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import ShoppingCartOutlined from "@mui/icons-material/ShoppingCartOutlined";
import { useDispatch } from "react-redux";

// Components
import KogInputTextSecondary from "@/main/components/shared/inputText/inputTextSecondary";
import KOGBadge from "@/main/components/shared/badge/badge";
import TypographyVehicle from "@/main/components/shared/typographyVehicle/typographyVehicle";
import { SkeletonLoading, SpinnerLoading } from "@/main/components/shared/styles";
import TypographyLabelText from "@/main/components/common/typography/typographyLabelText/typographyLabelText";
import QuoteProcurementPartInfo from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo";
import TypographyLabelValue from "@/main/components/common/typography/typographyLabelValue/typographyLabelValue";

// Utils
import { getTranslateByKey, isNullUndefined, isShowControlByElementId } from "@/main/services/utils";
import { formatCurrency } from "@/main/services/utilsNumber";
import { useCartReducer } from "@/main/store/cart";
import { OrderStepsEnum } from "@/main/services/enum/OrderStepsEnum";
import { useCreateOrderContext } from "../../../context";
import { Controls } from "@/main/services/constants/constantsControl";

// Styles
import * as SP from "../styles";
import * as S from "./styles";

// Redux
import { useUserReducer, saveUserPreferences, getUserPreferences } from "@/main/store/user";

// Models
import { quoteApi, userPreferencesApi } from "@/main/api";
import { UserPreferencesSaveRequest } from "@/main/models/request/userPreferencesRequest";
import { GetQuoteItemRequest } from "@/main/models/request";

interface IHeaderSearchProps {
	openFilter: boolean;
	hasClearFilter: boolean;
	handleDrawerOpen: () => void;
	handleSearchValue: (value: string) => void;
	displayCart: () => void;
	isHide?: boolean;
	hasDrawer?: boolean;
	showSupplierParts: boolean;
}

function HeaderSearch({
	openFilter,
	hasClearFilter,
	handleSearchValue,
	handleDrawerOpen,
	displayCart,
	hasDrawer = true,
	showSupplierParts,
}: IHeaderSearchProps) {
	const userReducer = useUserReducer();
	const dispatch = useDispatch();
	const classes = SP.useStyles();
	const cartReducer = useCartReducer();
	const {
		savedData,
		guidQuote: guidQuoteContext,
		partGuid,
		externalPartId,
		dataQuoteItem,
		setDataQuoteItem,
	} = useCreateOrderContext();
	const [searchValue, setSearchValue] = useState("");
	const [isLoadingQuoteItem, setIsLoadingQuoteItem] = useState<boolean>(false);
	const showPrices = isShowControlByElementId(Controls.IdShowPrices);

	const totalPriceCart = formatCurrency(cartReducer.summary.total);
	const totalItemsCart = cartReducer.summary.totalParts;
	const savedDataStepOne = savedData.find((item) => item.idStep === OrderStepsEnum.VehicleInformation);

	async function loadInfoQuoteItem(guidQuote: string, guidPart: string, externalPartId: string | null) {
		setIsLoadingQuoteItem(true);

		const request: GetQuoteItemRequest = {
			guidQuote,
			guidPart: !isNullUndefined(guidPart) ? guidPart : null,
			externalPartId: !isNullUndefined(externalPartId) ? externalPartId : null,
		};

		quoteApi
			.getQuoteItem(request)
			.then((response) => {
				setDataQuoteItem(response.data.result);
			})
			.finally(() => {
				setIsLoadingQuoteItem(false);
			});
	}

	function handleSetUserPreferences(option: boolean) {
		let guidUserPreferences: string | null = null;
		if (userReducer.userPreference.preferences !== null) {
			if (userReducer.userPreference.preferences.guid) {
				guidUserPreferences = userReducer.userPreference.preferences.guid;
			}
		}

		const request: UserPreferencesSaveRequest = {
			guid: guidUserPreferences,
			isViewCard: option,
		};

		userPreferencesApi
			.saveUserPreferences(request)
			.then((response) => {
				const { guid } = response.data.result;
				saveUserPreferencesAction(String(guid), option);
			})
			.catch(() => {
				saveUserPreferencesAction(guidUserPreferences, !option);
			});
	}

	useEffect(() => {
		dispatch(getUserPreferences());
	}, []);

	useEffect(() => {
		if (showSupplierParts && guidQuoteContext) {
			loadInfoQuoteItem(guidQuoteContext, partGuid, externalPartId);
		}
	}, [showSupplierParts, guidQuoteContext, partGuid, externalPartId]);

	useEffect(() => {
		if (!hasClearFilter) return;
		setSearchValue("");
	}, [hasClearFilter]);

	function saveUserPreferencesAction(guid: string | null, isViewCard: boolean) {
		dispatch(
			saveUserPreferences({
				guid: guid,
				isViewCard: isViewCard,
			}),
		);
	}

	const isLoadingCart = cartReducer.cartItems.isLoading || cartReducer.cartItems.isLoadingUpdate;
	const priceCap = dataQuoteItem?.priceCap ? formatCurrency(dataQuoteItem.priceCap) : "";

	const showPartInfo =
		showSupplierParts &&
		!isNullUndefined(dataQuoteItem) &&
		!isLoadingQuoteItem &&
		!isNullUndefined(dataQuoteItem.quoteProcurementPartInfo);

	const showPriceCap = isShowControlByElementId(Controls.IdShowPrices);

	return (
		<Toolbar
			className={clsx(classes.toolBar, {
				[classes.toolbarShift]: openFilter,
			})}
		>
			<S.AreaHeader showSupplierParts={showSupplierParts}>
				{hasDrawer && (
					<IconButton
						color="inherit"
						aria-label="open drawer"
						onClick={handleDrawerOpen}
						className={clsx(openFilter && classes.hide)}
						style={{ marginBottom: showSupplierParts ? "0" : "8px" }}
						id="id-createOrder-button-openFilter"
					>
						<MenuIcon />
					</IconButton>
				)}
				<S.WrapperHeader>
					<S.AreaInfoPart>
						<TypographyVehicle
							manufacturer={savedDataStepOne?.data?.vehicleManufacturer}
							model={savedDataStepOne?.data?.vehicleModel}
							subModel={savedDataStepOne?.data?.vehicleSubModel}
							year={savedDataStepOne?.data?.vehicleYear}
							licensePlate={""}
						/>
						{showPartInfo && (
							<>
								<TypographyLabelValue
									label={getTranslateByKey("information.partRequested")}
									value={<QuoteProcurementPartInfo data={dataQuoteItem.quoteProcurementPartInfo} renderType="row" />}
									isLoading={isLoadingQuoteItem}
								/>

								{showPriceCap && (
									<TypographyLabelText
										label={getTranslateByKey("information.priceCap")}
										text={priceCap}
										isLoading={isLoadingQuoteItem}
									/>
								)}
							</>
						)}
					</S.AreaInfoPart>
					<KogInputTextSecondary
						label={getTranslateByKey("information.search")}
						id="id-createOrder-input-search"
						customIcon={<SearchOutlined />}
						value={searchValue}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setSearchValue(event.target.value);
							handleSearchValue(event.target.value);
						}}
						hidden={showSupplierParts}
					/>
				</S.WrapperHeader>
				<S.WrapperViewMode>
					<IconButton onClick={() => handleSetUserPreferences(true)}>
						<AppsIcon className={userReducer.userPreference.preferences.isViewCard ? "active" : ""} fontSize="large" />
					</IconButton>
					<IconButton onClick={() => handleSetUserPreferences(false)}>
						<ReorderIcon
							className={!userReducer.userPreference.preferences.isViewCard ? "active" : ""}
							fontSize="large"
						/>
					</IconButton>
				</S.WrapperViewMode>
				<S.WrapperCart>
					<IconButton onClick={displayCart} style={{ position: "relative" }} id="id-createOrder-button-itemsCart">
						<ShoppingCartOutlined fontSize="large" />
						<KOGBadge content={isLoadingCart ? <SpinnerLoading customSize="5px" /> : totalItemsCart} isAbsolute />
					</IconButton>
					{showPrices && (
						<S.SubtotalCart>
							<p>{getTranslateByKey("information.cart")}</p>
							{isLoadingCart ? (
								<SkeletonLoading $forceWidth="70px" />
							) : (
								<S.SubtotalText>{totalPriceCart}</S.SubtotalText>
							)}
						</S.SubtotalCart>
					)}
				</S.WrapperCart>
			</S.AreaHeader>
		</Toolbar>
	);
}

export default HeaderSearch;
